/**
 * Utility functions for EAEU (Eurasian Economic Union) countries logic
 * 
 * EAEU countries:
 * - Russia (countryCode: 1)
 * - Kazakhstan (countryCode: 48)
 * - Belarus (countryCode: 42)
 * - Kyrgyzstan (countryCode: 15)
 * - Armenia (countryCode: 41)
 */

// EAEU country codes
export const EAEU_COUNTRY_CODES = [1, 42, 48, 15, 41];

// Countries where payment on receipt is allowed
export const PAYMENT_ON_RECEIPT_ALLOWED = [1, 48]; // Russia and Kazakhstan only

/**
 * Check if a country code belongs to EAEU
 * @param {number} countryCode - Country code to check
 * @returns {boolean} - True if country is in EAEU
 */
export function isEAEUCountry(countryCode) {
  return EAEU_COUNTRY_CODES.includes(countryCode);
}

/**
 * Check if either from or to city is in EAEU (but not both in Russia)
 * @param {number} cityFromCountryCode - From city country code
 * @param {number} cityToCountryCode - To city country code
 * @returns {boolean} - True if EAEU logic should apply
 */
export function shouldApplyEAEULogic(cityFromCountryCode, cityToCountryCode) {
  // If we don't have both country codes, can't determine
  if (cityFromCountryCode === null || cityFromCountryCode === undefined ||
      cityToCountryCode === null || cityToCountryCode === undefined) {
    return false;
  }

  // Check if at least one city is in EAEU
  const fromIsEAEU = isEAEUCountry(cityFromCountryCode);
  const toIsEAEU = isEAEUCountry(cityToCountryCode);

  // EAEU logic applies if at least one city is in EAEU AND they're not both in Russia
  if ((fromIsEAEU || toIsEAEU) && !(cityFromCountryCode === 1 && cityToCountryCode === 1)) {
    return true;
  }

  return false;
}

/**
 * Check if payment on receipt is allowed for given cities
 * @param {number} cityFromCountryCode - From city country code
 * @param {number} cityToCountryCode - To city country code
 * @param {boolean} isKzContract - True if contract is from Kazakhstan (KZ), false if from Russia (RF)
 * @returns {boolean} - True if payment on receipt is allowed
 */
export function isPaymentOnReceiptAllowed(cityFromCountryCode, cityToCountryCode, isKzContract = false) {
  // If we don't have both country codes, can't determine
  if (cityFromCountryCode === null || cityFromCountryCode === undefined ||
      cityToCountryCode === null || cityToCountryCode === undefined) {
    return false;
  }

  // Rule 1: Payment on receipt is NOT allowed within Kazakhstan (48-48) with RF contract
  if (cityFromCountryCode === 48 && cityToCountryCode === 48 && !isKzContract) {
    return false;
  }
  
  // Rule 2: Payment on receipt is NOT allowed within EAEU (except RF and KZ) to EAEU (except RF and KZ)
  // EAEU countries: 1 (Russia), 48 (Kazakhstan), 42 (Belarus), 15 (Kyrgyzstan), 41 (Armenia)
  const otherEAEU = [42, 15, 41]; // Belarus, Kyrgyzstan, Armenia
  
  if (otherEAEU.includes(cityFromCountryCode) && otherEAEU.includes(cityToCountryCode)) {
    return false;
  }
  
  // Rule 3: In all other cases - allow if at least one city is in Russia or Kazakhstan
  // This covers:
  // - RF -> RF, KZ -> KZ (with KZ contract), RF <-> KZ
  // - RF <-> other EAEU, KZ <-> other EAEU
  return PAYMENT_ON_RECEIPT_ALLOWED.includes(cityFromCountryCode) ||
         PAYMENT_ON_RECEIPT_ALLOWED.includes(cityToCountryCode);
}

/**
 * Get default values for EAEU shipments
 * @param {function} t - Translation function (optional)
 * @returns {object} - Default values for goods
 */
export function getEAEUDefaultValues(t = null) {
  return {
    goodsName: t ? t('personal_items') : 'Личные вещи',
    article: '1',
    userType: 'another'
  };
}

/**
 * Check if EAEU fields were changed from default values
 * @param {object} invoiceData - Invoice data object
 * @param {function} t - Translation function (optional, but recommended for correct comparison)
 * @returns {boolean} - True if any EAEU field was changed
 */
export function checkEAEUFieldsChanged(invoiceData, t = null) {
  const isEAEU = shouldApplyEAEULogic(invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode);
  
  // If not EAEU, fields are not considered changed
  if (!isEAEU) {
    return false;
  }
  
  // Get defaults using the translation function (if provided)
  // This ensures we compare against the correct translated values for the current language
  const defaults = getEAEUDefaultValues(t);
  
  // Also get all possible default values for goods name (to handle language changes)
  // These are the known translations of 'personal_items' in all supported languages
  const possibleDefaultNames = [
    'Личные вещи',  // Russian
    'Жеке заттар',  // Kazakh
    'Personal items' // English (if supported)
  ];
  
  // Check user_type
  if (invoiceData.user_type !== defaults.userType) {
    return true;
  }
  
  // Check all goods in all places
  if (invoiceData.places && Array.isArray(invoiceData.places)) {
    for (const place of invoiceData.places) {
      if (place.goods && Array.isArray(place.goods)) {
        for (const good of place.goods) {
          // Check article - should always be '1' for EAEU defaults
          if (good.article !== defaults.article) {
            return true;
          }
          
          // Check name - compare against current language default AND all possible defaults
          // This handles the case where language changed between setting defaults and checking
          const currentDefaultName = defaults.goodsName;
          const matchesAnyDefault = good.name === currentDefaultName || 
                                    possibleDefaultNames.includes(good.name);
          
          if (!matchesAnyDefault) {
            return true;
          }
        }
      }
    }
  }
  
  return false;
}
